import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";

/** Validator for an operating area. */
export const OperatingAreaValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    name: t.string,
  }),
  t.partial({
    companyId: t.string,
    companyName: t.string,
    companyUnitId: t.string,
    companyUnitName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

/** Validator for an array of operating areas. */
export const OperatingAreaArrayValidator = t.array(OperatingAreaValidator);

export type OperatingArea = t.TypeOf<typeof OperatingAreaValidator>;
export type OperatingAreaArray = t.TypeOf<typeof OperatingAreaArrayValidator>;

/**
 * An operating area.
 */
@Exclude()
export class OperatingAreaEntity implements Entity {
  @Expose() id!: string;
  @Expose() name!: string;
  @Expose() companyId?: string
  @Expose() companyName?: string
  @Expose() companyUnitId?: string
  @Expose() companyUnitName?: string
  @Expose() regionId!: string;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'OPERATINGAREA';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for OperatingAreaEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(plain: OperatingArea): OperatingAreaEntity {
    return plainToInstance(
      OperatingAreaEntity,
      plain,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  loadPlainValue(): Promise<OperatingArea> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(OperatingAreaValidator, object);
  }

  asPlainValue(): OperatingArea {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(OperatingAreaValidator, object);
  }
}
